import type { Environment } from '@mdvip/shared/types/config';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights, DistributedTracingModes } from '@microsoft/applicationinsights-web';
import { getSessionId } from './correlation';

export const reactPlugin = new ReactPlugin();

let appInsights: ApplicationInsights | null = null;

export interface TelemetryConfig {
  cloudRole: string;
  portalType: string;
}

let telemetryConfig: TelemetryConfig = {
  cloudRole: 'portal-frontend',
  portalType: 'unknown',
};

export function configureTelemetry(config: TelemetryConfig) {
  telemetryConfig = config;
}

/**
 * Initialises Application Insights with W3C distributed tracing. Must be called
 * once after config.json is loaded. The connection string is supplied at runtime
 * via `AppConfig.APPLICATIONINSIGHTS_CONNECTION_STRING`; for local dev it falls
 * back to `VITE_APPLICATIONINSIGHTS_CONNECTION_STRING`. No-op when neither is set.
 *
 * Distributed tracing model:
 * - App Insights auto-tracks page views and creates dependency records for
 *   every outbound fetch.
 * - For cross-origin requests to the API host (scoped via
 *   `correlationHeaderDomains`), App Insights injects `traceparent`,
 *   `tracestate`, and `Request-Context` headers. The dependency's span ID
 *   matches the outgoing traceparent's parent-id, so the backend's
 *   operation_ParentId chains correctly in Azure Monitor.
 * - Cross-app correlation (frontend ↔ backend in the same AI resource) is
 *   resolved automatically via `Request-Context` exchange.
 * - Third-party analytics / marketing domains are NOT in the allowlist, so
 *   no correlation headers leak there (would otherwise break their CORS).
 */
export function initializeTelemetry(
  connectionString: string | undefined,
  environment?: Environment,
  apiBaseUrl?: string,
) {
  // Idempotent: guards against React StrictMode double-effects and any future
  // config retry path, which would otherwise leak the previous instance and
  // double-report telemetry.
  if (appInsights) return;

  const resolvedConnectionString = connectionString || import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING;
  if (!resolvedConnectionString) return;

  let apiHost: string | undefined;
  if (apiBaseUrl) {
    try {
      apiHost = new URL(apiBaseUrl).host;
    } catch {
      // Malformed apiBaseUrl disables CORS correlation but must not crash
      // telemetry init (which would propagate up through loadConfig).
      apiHost = undefined;
    }
  }

  appInsights = new ApplicationInsights({
    config: {
      connectionString: resolvedConnectionString,
      extensions: [reactPlugin],
      enableAutoRouteTracking: true,
      disableFetchTracking: false,
      // Pure W3C — drop legacy Request-Id header (Microsoft is deprecating it).
      distributedTracingMode: DistributedTracingModes.W3C,
      enableCorsCorrelation: true,
      correlationHeaderDomains: apiHost ? [apiHost] : [],
      enableRequestHeaderTracking: false,
      enableResponseHeaderTracking: false,
      enableAjaxPerfTracking: false,
      enableUnhandledPromiseRejectionTracking: true,
    },
  });

  appInsights.loadAppInsights();

  appInsights.addTelemetryInitializer((envelope) => {
    if (envelope.tags) {
      envelope.tags['ai.cloud.role'] = telemetryConfig.cloudRole;
    }
    if (envelope.data) {
      // `environment` is captured at init and intentionally not refetched per
      // envelope — config.json is loaded once at boot and is immutable for the
      // session. `telemetryConfig` (mutable via configureTelemetry) and
      // getSessionId() (mutable via rotateSessionId) are read fresh each call.
      envelope.data['deployment.environment'] = environment ?? 'development';
      envelope.data['service.portal'] = telemetryConfig.portalType;
      // session.id joins App Insights telemetry to OTel spans and backend logs.
      envelope.data['session.id'] = getSessionId();
    }
    return true;
  });
}

export function setUserContext(user: { userId: string; userType: string; memberId?: string; providerId?: string }) {
  if (appInsights) {
    const accountId = user.memberId || user.providerId;
    appInsights.setAuthenticatedUserContext(user.userId, accountId, true);
    appInsights.context.user.id = user.userId;
  }
}

export function clearUserContext() {
  if (appInsights) {
    appInsights.clearAuthenticatedUserContext();
  }
}

export function trackEvent(name: string, properties?: Record<string, string>, measurements?: Record<string, number>) {
  if (appInsights) {
    appInsights.trackEvent({ name, properties, measurements });
  }
}

export function trackException(error: Error, properties?: Record<string, string>) {
  if (appInsights) {
    appInsights.trackException({ exception: error, properties });
  }
}

export function trackPageView(name: string, uri?: string) {
  if (appInsights) {
    appInsights.trackPageView({ name, uri });
  }
}

export function trackMetric(name: string, average: number, properties?: Record<string, string>) {
  if (appInsights) {
    appInsights.trackMetric({ name, average }, properties);
  }
}

export function trackDependency(
  name: string,
  data: string,
  duration: number,
  success: boolean,
  responseCode: number,
  properties?: Record<string, string>,
) {
  if (appInsights) {
    appInsights.trackDependencyData({
      id: `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
      name,
      duration,
      success,
      responseCode,
      data,
      type: 'HTTP',
      properties,
    });
  }
}

export function flushTelemetry() {
  if (appInsights) {
    appInsights.flush();
  }
}

export { appInsights };
